import React, { useMemo } from 'react';

import { link, routes } from '@aline/routes';
import { Typography } from '@mui/material';
import { Link, matchPath, useLocation, useMatch } from 'react-router-dom';
import { communityTemplatePaths } from 'src/components/community/pages/CommunityPage';
import { FolderWithDocsIcon } from 'src/components/icons/FolderWithDocsIcon';
import type { Apollo } from 'src/gql/generated';
import { createCommunityCategoryURLId } from 'src/helpers';
import { useCommunityCategories } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  panel: {
    boxSizing: 'border-box' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    maxWidth: 240,
    minWidth: 240,
    padding: theme.spacing(1),
  },
}));

export function CommunityLeftPanel(): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.panel}>
      <CommunityCategories />
    </div>
  );
}

const useCategoriesStyles = makeStyles<CategoryButtonStyleProps>()((theme, { isSelected }) => ({
  aboveCategories: {
    marginBottom: theme.spacing(2),
  },
  button: {
    '&:hover': {
      backgroundColor: isSelected
        ? theme.appColors.selectedBackground
        : theme.appColors.hoverBackground,
    },
    alignItems: 'center',
    backgroundColor: isSelected ? theme.appColors.selectedBackground : undefined,
    borderRadius: 3,
    cursor: 'pointer',
    display: 'flex',
    padding: theme.spacing(0.5, 1),
    textDecoration: 'none',
  },
  buttonText: {
    fontWeight: isSelected ? 600 : undefined,
  },
  emojiContainer: {
    alignItems: 'center',
    display: 'flex',
    width: 24,
  },
  icon: {
    height: 16,
    width: 16,
  },
  title: {
    marginBottom: theme.spacing(1),
  },
}));

export function CommunityCategories(): JSX.Element {
  const { categoryId } = useCategoryId();
  const { isAtCommunityTemplates } = useIsAtCommunityTemplates();
  const { communityCategories } = useCommunityCategories({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const noCategorySelected = communityCategories.every(c => {
    const categoryURLId = createCommunityCategoryURLId(c);
    return categoryURLId !== categoryId;
  });
  const { classes } = useCategoriesStyles({ isSelected: noCategorySelected });
  const sortedCategories = useMemo(
    () => [...communityCategories].sort((a, b) => a.title.localeCompare(b.title)),
    [communityCategories]
  );

  return (
    <>
      <div className={classes.aboveCategories}>
        <Link
          className={classes.button}
          to={
            isAtCommunityTemplates ? link.CommunityDocumentIndex() : link.CommunityWorkflowIndex()
          }
        >
          <div className={classes.emojiContainer}>
            <FolderWithDocsIcon className={classes.icon} />
          </div>
          <Typography noWrap className={classes.buttonText}>
            All
          </Typography>
        </Link>
      </div>
      <Typography className={classes.title} variant="subtitle1">
        Categories
      </Typography>
      {sortedCategories.map(category => {
        return <CategoryButton key={category.id} category={category} />;
      })}
    </>
  );
}

interface CategoryButtonStyleProps {
  isSelected: boolean;
}

interface CategoryButtonProps {
  category: Pick<Apollo.CommunityCategory, 'emoji' | 'id' | 'title' | '__typename'>;
}

export function CategoryButton({ category }: CategoryButtonProps): JSX.Element {
  const { categoryId } = useCategoryId();
  const { isAtCommunityTemplates } = useIsAtCommunityTemplates();
  const categoryURLId = createCommunityCategoryURLId(category);
  const { classes } = useCategoriesStyles({ isSelected: categoryURLId === categoryId });

  return (
    <Link
      className={classes.button}
      to={
        isAtCommunityTemplates
          ? link.CommunityCategoryDocuments({ categoryId: categoryURLId })
          : link.CommunityCategoryWorkflows({ categoryId: categoryURLId })
      }
    >
      <div className={classes.emojiContainer}>{category.emoji}</div>
      <Typography noWrap className={classes.buttonText}>
        {category.title}
      </Typography>
    </Link>
  );
}

export function useIsAtCommunityTemplates(): { isAtCommunityTemplates: boolean } {
  const location = useLocation();

  return {
    isAtCommunityTemplates: Boolean(
      communityTemplatePaths.some(path => matchPath({ end: false, path }, location.pathname))
    ),
  };
}

export function useCategoryId() {
  const match = useMatch(routes.CommunityCategoryIndex);
  return { categoryId: match?.params.categoryId };
}
